import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { pushModal } from "../../../lib/modalStack";

export interface LightboxItem {
  key: string;
  url: string;
  label: string;
  meta?: string;
}

interface Props {
  items: LightboxItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  open: boolean;
}

/** Minimum horizontal drag, in px, before a swipe pages instead of being read as a tap. */
const SWIPE_THRESHOLD = 50;

/**
 * Full-screen image viewer. The worker looks at photos, never saves them — there is deliberately
 * no download affordance here, unlike `AttachmentsSection`'s form-mode rows.
 *
 * Built directly on `createPortal` + `pushModal` rather than the shared `Modal`: `Modal.module.css`
 * hard-codes a centred white card with `padding: 30px; border-radius: 30px; max-height: 90dvh`,
 * which a full-bleed viewer would have to fight with `!important`s. `pushModal` is the part worth
 * reusing — Escape ownership and the Tab focus trap, already correct for a dialog stacked over
 * another (a lightbox opened from a modal, say).
 */
export const Lightbox: React.FC<Props> = ({ items, index, onIndexChange, onClose, open }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const [dragX, setDragX] = useState(0);

  const total = items.length;
  const current = items[index];

  const goTo = useCallback(
    (next: number) => {
      if (total === 0) return;
      onIndexChange((next + total) % total);
    },
    [onIndexChange, total],
  );

  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    return pushModal({
      onEscape: () => closeRef.current(),
      panel: () => panelRef.current,
      trapFocus: true,
    });
  }, [open]);

  // Focus the panel on open, return focus to whatever opened it on close — same contract as Modal.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      if (previous && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open]);

  // Body scroll is the page's, and a full-bleed overlay must not let it scroll behind the image.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goTo(index - 1);
      if (e.key === "ArrowRight") goTo(index + 1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, index, goTo]);

  if (!open || !current) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    setDragX(e.touches[0].clientX - touchStartX.current);
  };
  const handleTouchEnd = () => {
    if (Math.abs(dragX) > SWIPE_THRESHOLD) {
      goTo(dragX < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
    setDragX(0);
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={current.label}
      tabIndex={-1}
      className="fixed inset-0 z-[200] flex flex-col bg-black/95 outline-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex items-center justify-between gap-2 px-2 text-white"
        style={{
          paddingTop: "max(0.5rem, env(safe-area-inset-top))",
          paddingLeft: "max(0.5rem, env(safe-area-inset-left))",
          paddingRight: "max(0.5rem, env(safe-area-inset-right))",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10"
        >
          <X size={22} aria-hidden="true" />
        </button>
        {total > 1 && (
          <span className="text-sm tabular-nums text-white/80">
            {index + 1} / {total}
          </span>
        )}
      </div>

      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        style={{ touchAction: "pan-y pinch-zoom" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {total > 1 && (
          <button
            type="button"
            onClick={() => goTo(index - 1)}
            aria-label="Vorheriges Bild"
            className="absolute left-1 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10 max-[640px]:hidden"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
        )}

        <img
          key={current.key}
          src={current.url}
          alt={current.label}
          className="max-h-full max-w-full select-none object-contain"
          style={{ maxHeight: "100dvh" }}
          draggable={false}
        />

        {total > 1 && (
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            aria-label="Nächstes Bild"
            className="absolute right-1 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10 max-[640px]:hidden"
          >
            <ChevronRight size={24} aria-hidden="true" />
          </button>
        )}
      </div>

      <div
        className="px-4 pt-2 text-center text-sm text-white/90"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        {current.label}
        {current.meta && <span className="text-white/60"> · {current.meta}</span>}
      </div>
    </div>,
    document.body,
  );
};
